import { useTenantNavigation } from "../../hooks/useTenantNavigation.js";
import { Button, Flex, Modal, Typography, Form, Checkbox, Input, message, Steps } from "antd";
import { ArrowLeftOutlined, ArrowRightOutlined, PlusOutlined, UserOutlined, CheckCircleFilled, InteractionOutlined, HomeOutlined, SearchOutlined, FolderOutlined } from "@ant-design/icons";
import { spaceColorBgMapping } from "../../utils/colorBgColorMap";
import SvgIcons from "../layout/assets/SvgIcons";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import { showLoader } from "../../utils/showloader";
import { createSpace } from "../../services/SpaceServices";
import { useDispatch, useSelector } from "react-redux";
import { setCurrentMaster } from "../../redux/slices/masterDataSlice";
import { createDatabase, getMasterList } from "../../services/adminServices";
import MasterList from "./masterData/MasterList";
import TextArea from "antd/es/input/TextArea";
import { fetchUserInfo } from "../../redux/actions/userAction";
import BreadCrumb from "../BreadCrumb";
import axios from "../../axios-interceptor.js";
import { trimStringValues } from "../../utils/trimStrings";

const { Title } = Typography;

const BACKEND_API = process.env.REACT_APP_BACKEND_API;

const copyOptions = [
    { label: "Space Settings", value: "copySpaceSettings" },
    { label: "Space Members", value: "copySpaceMembers" },
    { label: "Workflow", value: "copyWorkflow" },
    { label: "Workflow Conditions", value: "copyWorkflowConditions" },
    { label: "Forms", value: "copyForms" },
    { label: "Form Conditions", value: "copyFormConditions" },
    { label: "Notification", value: "copyNotification" },
];

const AdminConfig = () => {
    const navigate = useTenantNavigation();
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { userInfo, userGroupSpaces } = useSelector(state => state.user);

    const [modalTitle, setModalTitle] = useState(t("Create a space"))
    const [openCreateModal, setOpenCreateModal] = useState(false)  //var to open create space modal
    const [createNew, setCreateNew] = useState(false)
    const [successModal, setSuccessModal] = useState(false);
    const [createdSpaceId, setCreatedSpaceId] = useState("")
    const [dataList, setDataList] = useState([])
    const [filteredData, setFilteredData] = useState([]);
    const [searchText, setSearchText] = useState("");

    // Template-related states
    const [showTemplateList, setShowTemplateList] = useState(false)
    const [selectedTemplateSpace, setSelectedTemplateSpace] = useState(null)
    const [templateSearchText, setTemplateSearchText] = useState("")
    const [spaces, setSpaces] = useState([])
    const [showCopySteps, setShowCopySteps] = useState(false)
    const [currentStep, setCurrentStep] = useState(0)
    const [optionsToBeCopied, setOptionsToBeCopied] = useState([])
    const [copyFormData, setCopyFormData] = useState({})

    const [form] = Form.useForm();
    const formValues = Form.useWatch([], form);
    const [copyForm] = Form.useForm();

    const [createMasterModal, setCreateMasterModal] = useState(false)
    const [masterForm] = Form.useForm();

    const getData = async () => {
        showLoader(true)
        let response = await getMasterList();
        showLoader(false)
        if (response?.data) {
            setDataList(response.data)
            setFilteredData(response.data)
        } else {
            setDataList([])
            setFilteredData([])
        }
    }

    useEffect(() => {
        getData();
    }, [])

    useEffect(() => {
        if (searchText) {
            const lowered = searchText.toLowerCase();
            const f = dataList.filter(md => md.dataName?.toLowerCase().includes(lowered));
            setFilteredData(f);
        } else {
            setFilteredData(dataList);
        }
    }, [searchText, dataList]);

    // Get spaces from userInfo and userGroupSpaces
    useEffect(() => {
        if (userInfo && userInfo.userId) {
            let reqSpaces = [];
            userInfo?.spaceInfo?.forEach(space => {
                reqSpaces.push(space)
            })
            userGroupSpaces?.forEach(groupSpace => {
                if (groupSpace.space?.length) {
                    groupSpace.spaceDetails?.forEach(space => {
                        let currentSpaceInReqSpace = reqSpaces.find(reqSpace => reqSpace.spaceId === space.spaceId)
                        if (!currentSpaceInReqSpace) {
                            reqSpaces.push(space)
                        }
                    })
                }
            })
            setSpaces([...reqSpaces])
        }
    }, [userInfo, userGroupSpaces]);

    const handleSpaceModal = () => {
        setModalTitle(t("Create a space"));
        setOpenCreateModal(true)
    }

    const handleSpaceBack = () => {
        setModalTitle(t("Create a space"))
        setOpenCreateModal(true)
        setCreateNew(false)
        form.resetFields();
    }

    const handleNewSpaceClick = () => {
        let title = <Flex onClick={handleSpaceBack} className="cursor-pointer">
            <ArrowLeftOutlined />
            <Title level={4} className="text-weight-600" style={{ margin: "0 0 0 0.5rem" }}>{t("Create a space")}</Title>
        </Flex>
        setModalTitle(title)
        setCreateNew(true)
    }

    const handleTemplateClick = () => {
        setShowTemplateList(true)
        let title = <Flex onClick={handleTemplateBack} className="cursor-pointer">
            <ArrowLeftOutlined />
            <Title level={4} className="text-weight-600" style={{ margin: "0 0 0 0.5rem" }}>{t("Existing Template")}</Title>
        </Flex>
        setModalTitle(title)
    }

    const handleTemplateBack = () => {
        setShowTemplateList(false)
        setTemplateSearchText("")
        setModalTitle(t("Create a space"))
    }

    const handleSelectTemplate = (space) => {
        setSelectedTemplateSpace(space)
        setShowTemplateList(false)
        setShowCopySteps(true)
        setCurrentStep(0)
        copyForm.setFieldsValue({
            name: space.name + " " + t("Copy"),
            description: space.description || ""
        })
        let title = <Flex onClick={() => { setShowCopySteps(false); handleTemplateClick(); }} className="cursor-pointer">
            <ArrowLeftOutlined />
            <Title level={4} className="text-weight-600" style={{ margin: "0 0 0 0.5rem" }}>{t("Copy from Template")}</Title>
        </Flex>
        setModalTitle(title)
    }


    const handleModalClose = () => {
        setOpenCreateModal(false);
        setCreateNew(false);
        setSuccessModal(false);
        setShowTemplateList(false);
        setShowCopySteps(false);
        setSelectedTemplateSpace(null);
        setTemplateSearchText("");
        setCurrentStep(0);
        setOptionsToBeCopied([]);
        setCopyFormData({});
        setModalTitle('');
        form.resetFields();
        copyForm.resetFields();
    }

    const handleNextStep = () => {
        const values = copyForm.getFieldsValue()
        setCopyFormData(values)
        setCurrentStep(1)
    }

    const submitForm = async () => {
        showLoader(true)
        let postData = trimStringValues({ name: formValues.name, description: formValues.description, defaultLanguage: "English", olderVersion: true, allowedDuplicate: false, enableChatroom: true, estimationEngineRequired: formValues.estimationEngineRequired || false, allowAllAccess: false })
        let resp = await createSpace(postData, userInfo);
        showLoader(false)
        if (resp.status === 200) {
            setCreatedSpaceId(resp.data.spaceId)
            setCreateNew(false);
            setModalTitle("")
            setSuccessModal(true)
            dispatch(fetchUserInfo())
        } else {
            message.error(resp.message)
        }
        dispatch(fetchUserInfo())
    }

    const submitCopySpace = async () => {
        try {
            // Validate abbreviation field
            if (!copyFormData.abbreviation) {
                message.error(t("Space abbreviation is required"))
                return
            }

            let postData = trimStringValues({
                spaceId: selectedTemplateSpace.spaceId,
                name: copyFormData.name,
                abbreviation: copyFormData.abbreviation.toUpperCase()
            })

            // Add all copy options to the payload
            copyOptions.forEach(opt => {
                postData[opt.value] = optionsToBeCopied.includes(opt.value)
            })

            showLoader(true)

            const response = await axios({
                method: "POST",
                url: `${BACKEND_API}/space-copy`,
                data: postData,
                headers: { userId: userInfo.userId }
            })

            showLoader(false)

            // Handle response based on the correct structure: {result: {...}, message: "...", status: 200}
            if (response.data && response.data.status === 200 && response.data.result) {
                message.success(response.data.message || t("Space copied successfully"))
                setCreatedSpaceId(response.data.result.spaceId)
                setShowCopySteps(false)
                setModalTitle("")
                setSuccessModal(true)
                dispatch(fetchUserInfo())
            } else {
                message.error(response.data?.message || t("Failed to copy space"))
            }
        } catch (error) {
            showLoader(false)
            const errorMessage = error.response?.data?.message || error.response?.data || error.message || t("Failed to copy space")
            message.error(errorMessage)
        }
    }

    const navigateToSpace = () => {
        navigate(`/app/space/${createdSpaceId}/space-settings`)
    }

    const handleNavigation = (path) => {
        navigate(path)
    }

    // Master
    const handleMasterDataModal = () => {
        setCreateMasterModal(true)
    }

    const closeMasterDataModal = () => {
        setCreateMasterModal(false)
        masterForm.resetFields()
    }

    const submitMasterForm = async (values) => {
        showLoader(true)
        let postResponse = await createDatabase(trimStringValues({ ...values, data: [], fields: [] }))
        showLoader(false)
        if (postResponse.status === 200) {
            message.success(postResponse.message)
            await getData();
        } else {
            message.error(postResponse.message)
            return
        }
        closeMasterDataModal();
    }

    return (
        <div className="px-10 py-4">
            {/* Admin Controls */}
            <BreadCrumb items={[{ title: <HomeOutlined />, path: "/app/dashboard" }, { title: t("Admin") }]} marginb="12px" />
            <Flex vertical style={{ marginBottom: "40px" }}>
                <Title className="text-h2 text-weight-600" style={{ marginTop: "0", marginBottom: "16px" }}>
                    {/* <ArrowLeftOutlined className="cursor-pointer" style={{ marginRight: "1rem", color: "#141BCB" }} onClick={() => navigate("/app/dashboard")} />  */}
                    {t('Admin Controls')}
                </Title>
                <Flex className="justify-between flex-wrap gap-4">
                    {/* <SpaceCreation spaces={spaces} /> */}
                    <div className="admin-action-card" onClick={handleSpaceModal}>
                        <Flex gap={12} align="center">
                            <div className="item-count"><PlusOutlined /></div>
                            <div className="admin-action-card-title">{t('Create a new space')}</div>
                        </Flex>
                        <div className="icon-medium">
                            <ArrowRightOutlined />
                        </div>
                    </div>
                    <div className="admin-action-card" onClick={() => handleNavigation("/app/admin/user-role")}>
                        <Flex gap={12} align="center">
                            <div className="item-count"><UserOutlined /></div>
                            <div className="admin-action-card-title">{t('User & role management')}</div>
                        </Flex>
                        <div className="icon-medium">
                            <ArrowRightOutlined />

                        </div>
                    </div>
                    <div className="admin-action-card" onClick={() => handleNavigation("/app/admin/activity-monitoring")}>
                        <Flex gap={12} align="center">
                            <div className="item-count"><InteractionOutlined /></div>
                            <div className="admin-action-card-title">{t('Activity Monitoring')}</div>
                        </Flex>
                        <div className="icon-medium">
                            <ArrowRightOutlined />

                        </div>
                    </div>
                    {/* <div className="admin-action-card" onClick={() => handleNavigation("/app/admin/configuration/organisation-structure")}>
                        <Flex gap={12} align="center">
                            <div className="item-count"><SettingOutlined /></div>
                            <div className="admin-action-card-title">{t('Configuration')}</div>
                        </Flex>
                        <div className="icon-medium">
                            <ArrowRightOutlined />

                        </div>
                    </div> */}
                </Flex>
            </Flex>

            <div>
                <Flex justify="space-between" align="center" style={{ marginBottom: "1.25rem" }}>
                    <Title className="text-h2 text-weight-600" style={{ marginTop: "0", marginBottom: 0 }}>{t('Master Data')}</Title>
                    <Input
                        placeholder={t("Search master data...")}
                        allowClear
                        onChange={(e) => setSearchText(e.target.value)}
                        suffix={<SearchOutlined className="mr-2 text-gray-400" />}
                        className="w-full md:w-72"
                        size="middle"
                        value={searchText}
                    />
                    {/* <Button type="link" onClick={() => navigate('/app/admin/master-data/all')} style={{ fontWeight: 600, paddingRight: 0 }}>{t('View All')}</Button> */}
                </Flex>
                <Flex align="center" wrap="wrap" gap={28}>
                    <div className="master-block cursor-pointer" onClick={handleMasterDataModal}>
                        <img src="/assets/MDIcon1.svg" className="master-icon1" />
                        <img src="/assets/MDIcon2.svg" className="master-icon2" />
                        <Button size="large" icon={<PlusOutlined className="blue-icon" />} />
                        <Title level={5} className="text-weight-600" style={{ marginTop: "0" }}>{t("Create a master data")}</Title>
                        <p className="text-color-tertiary" style={{ margin: "0.5rem 0" }}>{t("Create a master data to ensure accurate information")}</p>
                    </div>
                    <MasterList dataList={filteredData} getData={getData} />
                </Flex>
            </div>


            <Modal title={modalTitle} className="create-space-modal" open={openCreateModal} onOk={handleModalClose} onCancel={handleModalClose} footer={false}>

                {
                    createNew ?
                        <Form form={form} onFinish={submitForm} layout="vertical" style={{ padding: "1rem 0" }}>
                            <Form.Item
                                label={t("Space Name")}
                                name="name"
                                rules={[
                                    { required: true, whitespace: true, message: t("Space name is required") },
                                    { max: 50, message: t("Maximum 50 characters are allowed") }
                                ]}
                            >
                                <Input placeholder={t("Enter space name")} size="large" maxLength={50} showCount />
                            </Form.Item>
                            <Form.Item label={t("Description")} name="description" >
                                <TextArea placeholder={t("Enter description")} size="large" />
                            </Form.Item>
                            {/* <Form.Item label="Estimation Engine Required" name="estimationEngineRequired" valuePropName="checked" className="horizontal-form-item">
                                <Checkbox />
                            </Form.Item> */}
                            <Flex gap={16} justify="flex-end" style={{ marginTop: "1.25rem" }}>
                                <Button onClick={handleSpaceBack}>{t("Cancel")}</Button>
                                <Button htmlType="submit" className="submit-button">{t("Create Space")}</Button>
                            </Flex>
                        </Form>

                        : successModal ?
                            <Flex vertical align="center" style={{ padding: "1.5rem" }}>
                                <CheckCircleFilled style={{ color: "#52C41A", fontSize: "63px" }} />
                                <Typography.Title level={4}>{t("Congratulations!")}</Typography.Title>
                                <Typography.Paragraph style={{ opacity: "45%" }}>
                                    {t("The space has been created successfully.")}
                                </Typography.Paragraph>
                                <Flex gap={16}>
                                    <Button className="submit-button" size="large" onClick={navigateToSpace}>
                                        {t("Take me to space")}
                                    </Button>
                                    <Button type="default" onClick={handleModalClose} size="large">
                                        {t("Cancel")}
                                    </Button>
                                </Flex>
                            </Flex>

                            : showTemplateList ?
                                <div style={{ padding: "4px 0" }}>
                                    <Input
                                        placeholder={t("Search template")}
                                        prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
                                        value={templateSearchText}
                                        onChange={(e) => setTemplateSearchText(e.target.value)}
                                        style={{ marginBottom: "20px", borderRadius: "8px" }}
                                        size="large"
                                        allowClear
                                    />
                                    <div style={{ maxHeight: "380px", overflowY: "auto", paddingRight: "4px" }}>
                                        {spaces
                                            .filter(space => space.name?.toLowerCase().includes(templateSearchText.toLowerCase()))
                                            .map(space => {
                                                const spaceColor = space.spaceColor || "#141BCB";
                                                const bgColor = spaceColorBgMapping[spaceColor] || "#e8e9fd";
                                                const initials = space.name?.slice(0, 2).toUpperCase() || "SP";

                                                return (
                                                    <Flex
                                                        key={space.spaceId}
                                                        className="template-card"
                                                        align="center"
                                                        gap={14}
                                                        onClick={() => handleSelectTemplate(space)}
                                                        style={{
                                                            padding: "14px 16px",
                                                            border: "1px solid #f0f0f0",
                                                            borderRadius: "10px",
                                                            marginBottom: "10px",
                                                            cursor: "pointer",
                                                            transition: "all 0.2s ease",
                                                            background: "#fff"
                                                        }}
                                                        onMouseEnter={(e) => {
                                                            e.currentTarget.style.borderColor = spaceColor;
                                                            e.currentTarget.style.boxShadow = `0 2px 12px ${spaceColor}20`;
                                                            e.currentTarget.style.transform = "translateY(-1px)";
                                                        }}
                                                        onMouseLeave={(e) => {
                                                            e.currentTarget.style.borderColor = "#f0f0f0";
                                                            e.currentTarget.style.boxShadow = "none";
                                                            e.currentTarget.style.transform = "translateY(0)";
                                                        }}
                                                    >
                                                        <div style={{
                                                            width: "44px",
                                                            height: "44px",
                                                            minWidth: "44px",
                                                            borderRadius: "10px",
                                                            backgroundColor: bgColor,
                                                            display: "flex",
                                                            alignItems: "center",
                                                            justifyContent: "center",
                                                            color: spaceColor
                                                        }}>
                                                            <SvgIcons type={space.spaceIcon || ""} />
                                                        </div>
                                                        <div style={{ flex: 1, minWidth: 0 }}>
                                                            <Title level={5} style={{
                                                                margin: 0,
                                                                fontSize: "15px",
                                                                fontWeight: 600,
                                                                color: "#262626",
                                                                whiteSpace: "nowrap",
                                                                overflow: "hidden",
                                                                textOverflow: "ellipsis"
                                                            }}>
                                                                {space.name}
                                                            </Title>
                                                            <p style={{
                                                                margin: "4px 0 0 0",
                                                                color: "#8c8c8c",
                                                                fontSize: "13px",
                                                                lineHeight: 1.4,
                                                                display: "-webkit-box",
                                                                WebkitLineClamp: 2,
                                                                WebkitBoxOrient: "vertical",
                                                                overflow: "hidden"
                                                            }}>
                                                                {space.description || t("Pre made workflows, forms, master data and estimation engine")}
                                                            </p>
                                                        </div>
                                                    </Flex>
                                                );
                                            })
                                        }
                                        {spaces.filter(space => space.name?.toLowerCase().includes(templateSearchText.toLowerCase())).length === 0 && (
                                            <Flex vertical align="center" justify="center" style={{ padding: "40px 20px", color: "#bfbfbf" }}>
                                                <FolderOutlined style={{ fontSize: "36px", marginBottom: "12px" }} />
                                                <p style={{ margin: 0, fontSize: "14px" }}>{t("No templates found")}</p>
                                            </Flex>
                                        )}
                                    </div>
                                </div>

                                : showCopySteps ?
                                    <div>
                                        <Steps progressDot current={currentStep} style={{ marginBottom: "24px" }} items={[
                                            { title: t("General Details") },
                                            { title: t("Copy Details") }
                                        ]} />

                                        {currentStep === 0 && (
                                            <Form form={copyForm} layout="vertical" onFinish={handleNextStep}>
                                                <Form.Item
                                                    label={t("Space Name")}
                                                    name="name"
                                                    rules={[
                                                        { required: true, whitespace: true, message: t("Space name is required") },
                                                        { max: 50, message: t("Maximum 50 characters are allowed") }
                                                    ]}
                                                >
                                                    <Input placeholder={t("Enter space name")} size="large" maxLength={50} showCount />
                                                </Form.Item>
                                                <Form.Item label={t("Space Abbreviation")} name="abbreviation" rules={[
                                                    { required: true, message: t("Space abbreviation is required") },
                                                    { max: 6, message: t("Maximum 6 characters") },
                                                    { min: 2, message: t("Minimum 2 characters") }
                                                ]}>
                                                    <Input placeholder={t("Enter abbreviation")} size="large" />
                                                </Form.Item>
                                                {/* <Form.Item label={t("Description")} name="description">
                                                    <TextArea placeholder={t("Enter description")} />
                                                </Form.Item> */}
                                                <Flex gap={16} justify="flex-end">
                                                    <Button onClick={() => { setShowCopySteps(false); handleTemplateClick(); }}>{t("Back")}</Button>
                                                    <Button htmlType="submit" className="submit-button">{t("Next")}</Button>
                                                </Flex>
                                            </Form>
                                        )}

                                        {currentStep === 1 && (
                                            <div>
                                                <div style={{ marginBottom: "16px" }}>
                                                    <label style={{ display: "block", marginBottom: "8px", fontWeight: 500 }}>{t("Select what to copy")}</label>
                                                    <Checkbox.Group onChange={setOptionsToBeCopied} value={optionsToBeCopied}>
                                                        <Flex vertical gap={8}>
                                                            {copyOptions.map(opt => (
                                                                <Checkbox key={opt.value} value={opt.value}>{t(opt.label)}</Checkbox>
                                                            ))}
                                                        </Flex>
                                                    </Checkbox.Group>
                                                </div>
                                                <Flex gap={16} justify="flex-end">
                                                    <Button onClick={() => setCurrentStep(0)}>{t("Back")}</Button>
                                                    <Button onClick={submitCopySpace} className="submit-button">{t("Copy Space")}</Button>
                                                </Flex>
                                            </div>
                                        )}
                                    </div>

                                    :
                                    <Flex gap={16}>
                                        <Flex justify="center" align="center" vertical className="create-space-card" onClick={handleNewSpaceClick}>
                                            <img src="/assets/CreateNewSpace.svg" width="70" height="67" />
                                            <Title level={5} className="text-weight-600" style={{ marginTop: "12px" }}>{t("Create a new space")}</Title>
                                            <p className="text-color-tertiary" style={{ margin: "0", textAlign: "center" }}>{t("Create space from scratch, create workflows, forms, estimation engine.")}</p>
                                        </Flex>
                                        <Flex justify="center" align="center" vertical className="create-space-card" onClick={handleTemplateClick}>
                                            <img src="/assets/ExistingTemplate.svg" width="70" height="67" />
                                            <Title level={5} className="text-weight-600" style={{ marginTop: "12px" }}>{t("Use existing template")}</Title>
                                            <p className="text-color-tertiary" style={{ margin: "0", textAlign: "center" }}>{t("Create space with pre-made workflow, form, estimation engine.")}</p>
                                        </Flex>

                                    </Flex>
                }

            </Modal>

            <Modal title={t("Create a master data")} className="create-space-modal" open={createMasterModal} onOk={closeMasterDataModal} onCancel={closeMasterDataModal} footer={false}>


                <Form form={masterForm} onFinish={submitMasterForm} layout="vertical" style={{ padding: "1rem 0" }}>
                    <Form.Item label={t("Master Data Name")} name="dataName" rules={[
                        { required: true, whitespace: true, message: t("Master data name is required") },
                        { max: 50, message: t("Maximum 50 characters are allowed") }
                    ]}>
                        <Input placeholder={t("Enter master data name")} size="large" maxLength={50} showCount />
                    </Form.Item>
                    <Flex gap={16} style={{ marginTop: "1.5rem" }}>
                        <Button htmlType="submit" className="submit-button">{t("Create Master Data")}</Button>
                        <Button onClick={closeMasterDataModal}>{t("Cancel")}</Button>
                    </Flex>
                </Form>

            </Modal>
        </div>
    )
}

export default AdminConfig